/**
 * Shared Courier Utilities
 *
 * Courier-agnostic helpers used by DTDC, India Post, and (optionally) Shiprocket managers.
 * All code should use these functions instead of ad-hoc string matching.
 */

const FINAL_STATUSES = new Set([
  'DELIVERED',
  'RTO_DELIVERED',
  'CANCELLED',
  'LOST',
  'DAMAGED',
]);

const COURIER_DISPLAY = {
  shiprocket: 'Shiprocket',
  dtdc: 'DTDC',
  india_post: 'India Post',
  ekart: 'Ekart',
};

const COURIER_API_STATUS = {
  shiprocket: 'live',
  dtdc: 'api_pending',
  india_post: 'manual',
  ekart: 'live',
};

/**
 * Normalize courier names to consistent values across the entire application.
 * Canonical values: 'shiprocket' | 'dtdc' | 'india_post'
 * @param {string} courier
 * @returns {'shiprocket'|'dtdc'|'india_post'|''}
 */
export function normalizeCourier(courier) {
  if (!courier) return '';
  const c = String(courier).toLowerCase().replace(/\s|-/g, '_');
  if (c.includes('india') && c.includes('post')) return 'india_post';
  if (c.includes('dtdc')) return 'dtdc';
  if (c.includes('ekart')) return 'ekart';
  if (c.includes('shiprocket')) return 'shiprocket';
  return c;
}

/**
 * Extract the AWB / consignment / tracking ID from an order,
 * regardless of courier or field naming convention.
 * @param {Object} order
 * @returns {string}
 */
export function extractAwb(order) {
  if (!order) return '';
  return (
    order.tracking?.awb ||
    order.awb ||
    order.shipping?.shiprocket?.awb ||
    order.shipping?.dtdc?.awb ||
    order.shipping?.dtdc?.consignmentNumber ||
    order.shipping?.indiaPost?.trackingId ||
    order.shipping?.indiaPost?.barcodeNo ||
    order.shipping?.india_post?.awb ||
    order.shipping?.india_post?.trackingId ||
    order.shipping?.ekart?.awb ||
    order.shipping?.ekart?.tracking_id ||
    order.shipping?.ekart?.trackingId ||
    order.shipping?.awb ||
    order.trackingId ||
    order.consignmentNumber ||
    ''
  );
}

/**
 * Extract the courier name from an order, checking all possible paths.
 * Returns normalized value via normalizeCourier().
 * @param {Object} order
 * @returns {string}
 */
export function extractCourier(order) {
  if (!order) return '';
  const raw =
    order.tracking?.courier ||
    order.shipping?.courier ||
    order.courierPreference ||
    order.shipping?.courierPreference ||
    '';
  return normalizeCourier(raw);
}

/**
 * Check whether a tracking status is terminal (no further updates expected).
 * @param {string} status
 * @returns {boolean}
 */
export function isFinalStatus(status) {
  if (!status) return false;
  return FINAL_STATUSES.has(String(status).toUpperCase().replace(/\s+/g, '_'));
}

/**
 * Get human-readable display name for a normalised courier key.
 * @param {string} courier  Normalised key ('shiprocket', 'dtdc', 'india_post')
 * @returns {string}
 */
export function getCourierDisplayName(courier) {
  return COURIER_DISPLAY[normalizeCourier(courier)] || courier || 'Unknown';
}

/**
 * Get API integration status for a courier.
 * @param {string} courier  Normalised key
 * @returns {'live'|'api_pending'|'manual'}
 */
export function getCourierApiStatus(courier) {
  return COURIER_API_STATUS[normalizeCourier(courier)] || 'manual';
}
